/* Savings growth projection.
 *
 * Adds up every automatic monthly contribution (biweekly cadences converted to
 * monthly equivalents) + the user's "Savings" bucket total from the Budget page,
 * then projects the balance forward assuming a fixed 2.6% APY compounded monthly.
 * The chart shows the trajectory month-by-month for 30 years; milestone tiles
 * highlight 6 months / 1 year / 5 / 10 / 20 / 30 years.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, fmtMoney } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";
import { TrendingUp, Sparkles } from "lucide-react";

const FIXED_APY = 2.6;
// Biweekly (every 14 days) → 26 pulls per year → /12 = monthly equivalent.
const BIWEEKLY_TO_MONTHLY = 26 / 12;
// Every other paycheck (every 28 days) → 13 pulls per year → /12.
const EVERY_OTHER_PAYCHECK_TO_MONTHLY = 13 / 12;

function monthlyEquivalent(status) {
  if (!status?.applicable) return 0;
  const amt = Number(status.contribution_amount || 0);
  if (status.cadence === "biweekly_friday")      return amt * BIWEEKLY_TO_MONTHLY;
  if (status.cadence === "monthly_payday")       return amt;
  if (status.cadence === "every_other_paycheck") return amt * EVERY_OTHER_PAYCHECK_TO_MONTHLY;
  if (status.cadence === "monthly_first")        return amt;
  // Fallback: treat as monthly.
  return amt;
}

function buildProjection(monthlyContribution, apyPct, months = 360) {
  const r = (apyPct / 100) / 12;
  const points = [];
  let balance = 0;
  for (let m = 0; m <= months; m++) {
    if (m === 0) {
      points.push({ month: 0, balance: 0 });
      continue;
    }
    // Contribution first, then interest.
    balance += monthlyContribution;
    balance *= 1 + r;
    points.push({ month: m, balance });
  }
  return points;
}

function MilestoneTile({ months, projection, label }) {
  const point = projection.find(p => p.month === months);
  const value = point ? point.balance : 0;
  return (
    <div className="bg-linen border border-sand rounded-xl p-4"
         data-testid={`milestone-${months}mo`}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-sage">{label}</div>
      <div className="font-mono text-2xl text-ink mt-1">{fmtMoney(value)}</div>
    </div>
  );
}

export default function SavingsProjectionCard({ budgetSavingsTotal = 0 }) {
  const { user } = useAuth();
  const [customs, setCustoms] = useState([]);
  const loadCustoms = () => api.get("/baby-funds").then(({ data }) => setCustoms((data || []).filter((b) => b.auto?.enabled).map((b) => ({ id: b.id, name: `Baby · ${b.name}`, amount: b.auto.amount, cadence: "monthly_payday" })))).catch(() => {});
  useEffect(() => { loadCustoms(); }, []); // eslint-disable-line
  const apy = FIXED_APY;
  const [boost, setBoost] = useState(0);

  const contributions = useMemo(() => {
    const rows = [
      { key: "retirement",    label: "Untouchable Retirement", status: user?.retirement_contribution?.enabled === false ? null : user?.retirement_contribution },
      { key: "job_loss",      label: "Rainy-day (Job Loss)",   status: user?.job_loss_contribution },
      { key: "car_emergency", label: "Car Emergency",           status: user?.car_emergency_contribution },
    ].map(r => ({ ...r, monthly: monthlyEquivalent(r.status) }))
     .filter(r => r.monthly > 0);
    customs.forEach((c) => {
      const cad = c.cadence || "";
      const perMonth = cad.includes("biweekly") || cad.includes("every_other") ? c.amount * 26 / 12
        : cad.includes("weekly") ? c.amount * 52 / 12 : c.amount;
      rows.push({ key: `custom:${c.id}`, label: `${c.name}${c.locked ? " · locked" : ""}`, status: null, monthly: Math.round(perMonth * 100) / 100, custom: true });
    });
    return rows;
  }, [user, customs]);

  const autoMonthly    = contributions.reduce((s, r) => s + r.monthly, 0);
  const budgetMonthly  = Number(budgetSavingsTotal) || 0;
  const totalMonthly   = autoMonthly + budgetMonthly + Number(boost || 0);

  const projection = useMemo(
    () => buildProjection(totalMonthly, apy, 360),
    [totalMonthly, apy],
  );

  const milestones = [
    { months: 6,   label: "6 months" },
    { months: 12,  label: "1 year" },
    { months: 60,  label: "5 years" },
    { months: 120, label: "10 years" },
    { months: 240, label: "20 years" },
    { months: 360, label: "30 years" },
  ];

  const decade1 = projection.find(p => p.month === 120)?.balance ?? 0;
  const decade3 = projection.find(p => p.month === 360)?.balance ?? 0;
  const contributedTotal = totalMonthly * 360;
  const interestEarned   = Math.max(0, decade3 - contributedTotal);

  // Format ticks: show years on the X axis
  const xTickFormatter = (m) => {
    if (m === 0) return "0";
    if (m % 60 === 0) return `${m / 12}y`;
    if (m === 12) return "1y";
    return "";
  };
  const yTickFormatter = (v) => {
    if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}k`;
    return `$${v.toFixed(0)}`;
  };

  const empty = totalMonthly <= 0;

  return (
    <section className="mt-12" data-testid="savings-projection">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Projection</div>
          <h2 className="font-serif text-2xl tracking-tight mt-1 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-forest" strokeWidth={1.75} />
            If you stick to the plan…
          </h2>
          <p className="text-xs text-moss mt-1 max-w-xl">
            Every automatic Friday pull and 1st-of-month deposit, plus whatever you allocate to Savings in your budget,
            adds up. Chart assumes a {apy.toFixed(1)}% APY compounded monthly.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-sage uppercase tracking-wider font-semibold">Yield</span>
          <span className="font-mono text-ink bg-white border border-sand rounded-md px-2 py-1" data-testid="apy-fixed">{apy.toFixed(1)}% APY</span>
        </div>
      </div>

      {/* Contribution breakdown */}
      <div className="bg-ink text-cream rounded-2xl p-5 sm:p-6 mb-4"
           data-testid="projection-summary">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold">
              Monthly savings power
            </div>
            <div className="font-mono text-3xl sm:text-4xl mt-1 text-white"
                 data-testid="monthly-total">{fmtMoney(totalMonthly)}</div>
            <div className="text-[10px] text-sage mt-0.5 uppercase tracking-wider">
              per month · combined
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-sage">
              In 30 years
            </div>
            <div className="font-mono text-3xl sm:text-4xl mt-1 text-white"
                 data-testid="thirty-year-total">{fmtMoney(decade3)}</div>
            <div className="text-[10px] text-mint-deep mt-0.5">
              +{fmtMoney(interestEarned)} in interest
            </div>
          </div>
        </div>
        {contributions.length > 0 && (
          <ul className="mt-4 pt-4 border-t border-ink-soft grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {contributions.map((r) => (
              <li key={r.key} className="flex items-center justify-between gap-2"
                  data-testid={`contribution-${r.key}`}>
                <span className="text-sage-light truncate">{r.label}</span>
                <span className="font-mono text-white">{fmtMoney(r.monthly)}/mo</span>
              </li>
            ))}
            {budgetMonthly > 0 && (
              <li className="flex items-center justify-between gap-2" data-testid="contribution-budget">
                <span className="text-sage-light truncate">Budget · Savings bucket</span>
                <span className="font-mono text-white">{fmtMoney(budgetMonthly)}/mo</span>
              </li>
            )}
            {boost > 0 && (
              <li className="flex items-center justify-between gap-2" data-testid="contribution-boost">
                <span className="text-gold truncate inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" strokeWidth={2} /> Boost
                </span>
                <span className="font-mono text-gold">+{fmtMoney(boost)}/mo</span>
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Boost slider — "what if I added more?" */}
      <div className="bg-linen border border-sand rounded-2xl p-4 sm:p-5 mb-4"
           data-testid="boost-slider">
        <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" strokeWidth={2} /> What if you added more?
            </div>
            <div className="text-xs text-moss mt-0.5">
              Drag to see what an extra chunk each month does over 30 years.
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-sage">Extra / month</div>
            <div className="font-mono text-2xl text-gold" data-testid="boost-value">
              +{fmtMoney(boost)}
            </div>
          </div>
        </div>
        <input type="range" min="0" max="1000" step="25" value={boost}
          onChange={(e) => setBoost(Number(e.target.value))}
          className="w-full accent-gold h-2 rounded-full bg-sand-warm cursor-pointer"
          data-testid="boost-slider-input"
          aria-label="Extra monthly savings amount" />
        <div className="flex justify-between text-[9px] uppercase tracking-wider text-sage font-mono mt-1">
          <span>$0</span><span>$250</span><span>$500</span><span>$750</span><span>$1,000</span>
        </div>
      </div>

      {empty ? (
        <div className="border border-dashed border-sand rounded-2xl p-8 text-center text-sm text-sage"
             data-testid="projection-empty">
          Add money to a savings bucket or activate your vault contributions to see the growth curve.
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="bg-linen border border-sand rounded-2xl p-4 sm:p-6"
               data-testid="projection-chart">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={projection} margin={{ top: 10, right: 12, bottom: 6, left: 4 }}>
                  <XAxis dataKey="month" tickFormatter={xTickFormatter}
                    ticks={[0, 12, 60, 120, 240, 360]}
                    stroke="rgb(var(--c-sage))" fontSize={11} tick={{ fill: "rgb(var(--c-moss))" }} />
                  <YAxis tickFormatter={yTickFormatter}
                    stroke="rgb(var(--c-sage))" fontSize={11} tick={{ fill: "rgb(var(--c-moss))" }} width={56} />
                  <Tooltip
                    contentStyle={{ background: "rgb(var(--c-ink))", border: "none", borderRadius: 8,
                                    color: "rgb(var(--c-cream))", fontSize: 12 }}
                    labelStyle={{ color: "rgb(var(--c-gold))", fontSize: 11 }}
                    labelFormatter={(m) => `Month ${m} (${(m / 12).toFixed(1)} yrs)`}
                    formatter={(v) => [fmtMoney(v), "Projected balance"]} />
                  <Line type="monotone" dataKey="balance"
                    stroke="rgb(var(--c-forest))" strokeWidth={2} dot={false} isAnimationActive={false} />
                  {[12, 60, 120, 240, 360].map((m) => {
                    const p = projection.find(pt => pt.month === m);
                    return p ? (
                      <ReferenceDot key={m} x={m} y={p.balance} r={4}
                        fill="rgb(var(--c-gold))" stroke="rgb(var(--c-ink))" strokeWidth={1.5} />
                    ) : null;
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Milestone tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4"
               data-testid="milestone-tiles">
            {milestones.map((m) => (
              <MilestoneTile key={m.months} months={m.months}
                             projection={projection} label={m.label} />
            ))}
          </div>

          <div className="mt-4 text-[11px] text-sage inline-flex items-start gap-1.5">
            <Sparkles className="w-3 h-3 mt-0.5 text-gold" strokeWidth={2} />
            Projections assume every contribution lands on time and yields {apy.toFixed(1)}% APY compounded monthly. Real returns will vary.
          </div>
        </>
      )}
    </section>
  );
}
